/**
 * Stable cache-key derivation for memoizing `evaluateBlock` calls by
 * `(blockName, customProperties, visibilityState)`.
 *
 * One truth shared by every per-INSERT `evaluateBlock` caller that
 * memoizes results, so the key derivation can never drift between them:
 *
 *   - legacy SVG render path — `scene/blockRender.tsx`'s module-global
 *     `evalCache` (unbounded document lifetime, FIFO-trimmed).
 *   - typed scene3d render path — `scene3d/adapters/typed/blockFromEntity.ts`'s
 *     per-flatten-call memo (S10e; fresh `Map` per `blockFromEntity` /
 *     `flattenBlockReference` invocation — see that module's doc comment
 *     for why it is deliberately NOT module-global).
 *
 * Extracted out of `blockRender.tsx` (jscpd — CLAUDE.md "default on FAIL =
 * eliminate the clone"): both call sites need the IDENTICAL stable-sort +
 * join algorithm, and a hand-rolled second copy would silently drift the
 * moment one side changed the separator / sort order.
 */

/**
 * Stable, order-independent, UNAMBIGUOUS stringification of a
 * `customProperties` bag — sorts keys so `{a:1,b:2}` and `{b:2,a:1}`
 * produce the SAME cache key, and JSON-encodes the sorted `[key, value]`
 * pairs rather than hand-joining `key=value` with a bare `&` separator.
 *
 * (local Codex review, P2, pre-PR) — the original `&`/`=` join was
 * ambiguous: `{a: "x&b=y"}` and `{a: "x", b: "y"}` both serialized to the
 * SAME raw string `"a=x&b=y"`. When a flatten call resolves two DISTINCT
 * instances of the same dynamic block (e.g. two nested `Block` children,
 * or a MINSERT cell vs a sibling), a collision here means the memo
 * returns ONE instance's cached evaluated entities for the OTHER — a real
 * "wrong dynamic-block visibility/parameter state renders" bug, exactly
 * the class this cache exists to help fix (S10e), not reintroduce.
 * `JSON.stringify` on the sorted `[key, value][]` array escapes every
 * character unambiguously (quote/backslash/delimiter-lookalike), so two
 * DIFFERENT bags can never collide on the same key string.
 *
 * Empty / undefined bag → `"[]"`.
 */
export function stableStringifyCustomProperties(
  props: Readonly<Record<string, string | number | boolean>> | undefined,
): string {
  if (!props) return "[]";
  const keys = Object.keys(props).sort();
  return JSON.stringify(keys.map((k) => [k, props[k]]));
}

/** Minimal per-instance shape the key derivation needs — structurally
 *  compatible with `InsertInstanceLike` (`evaluator.ts`) without importing
 *  it, so this module has zero dependency on the evaluator internals. */
export interface EvaluateBlockCacheKeyInstance {
  readonly customProperties?: Readonly<
    Record<string, string | number | boolean>
  >;
  readonly visibilityState?: string;
}

/**
 * Build the `evaluateBlock` memo key. Two calls with the SAME block name +
 * customProperties (any key order) + visibilityState collide on the same
 * key — the memoized caller can safely skip re-running the evaluator.
 *
 * (local Codex review, P2, pre-PR) — JSON-encodes the full 3-tuple
 * `[blockName, propsKey, visibilityState]` rather than hand-joining with a
 * bare `|` separator, for the SAME reason `stableStringifyCustomProperties`
 * above JSON-encodes its pairs: a `blockName` or an already-JSON-encoded
 * `propsKey` that happens to contain a literal `|` could otherwise collide
 * with a DIFFERENT (blockName, propsKey) split at a different boundary
 * (e.g. `blockName="A", propsKey="B|C"` vs `blockName="A|B", propsKey="C"`
 * both naive-joined to `"A|B|C|..."`). `JSON.stringify` on the tuple
 * escapes every field unambiguously, closing the same collision class at
 * the outer layer too.
 */
export function buildEvaluateBlockCacheKey(
  blockName: string,
  instance: EvaluateBlockCacheKeyInstance,
): string {
  const propsKey = stableStringifyCustomProperties(instance.customProperties);
  return JSON.stringify([blockName, propsKey, instance.visibilityState ?? null]);
}
